// Mesa no computador (tela 5.5 do canvas): a escala da caixa do meio, que cresce com a coluna e deixa a mesa sempre
// inteira, sem rolagem, e as partes da coluna da esquerda (histórico da mão e seu trio).
import { useLayoutEffect, useRef, useState } from 'react';
import { estadoDaRodada, rankOf, suitOf, team } from '@truco6/regras';
import type { Card, Gesture, PlayerView, Seat, Team } from '@truco6/regras';
import type { LugarPublico } from '@truco6/mesa/protocolo';
import { Rosto } from '../comum/Rosto.tsx';
import { CARTA_DO_SINAL, GESTO_EMOJI, SIMBOLO, ehBot } from './textos.ts';

type Mao = NonNullable<PlayerView['hand']>;
export interface SinalRecebido { id: number; seat: Seat; gesture: Gesture }

// Quanto a caixa do meio (largura x altura, nas coordenadas da mesa) escala para caber na coluna. Devolve a escala e
// a ref da coluna (a coluna só aparece quando a mesa tem estado).
export function useEscalaDoCentro(largura: number, altura: number): [number, (el: HTMLElement | null) => void] {
  const [k, setK] = useState(1);
  const [el, setEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!el) return;
    const medir = () => {
      const w = el.clientWidth, h = el.clientHeight;
      if (w > 0 && h > 0) setK(Math.min(w / largura, h / altura));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, largura, altura]);
  return [k, setEl];
}

// "6♦", com o naipe vermelho em copas e ouros. A manilha aberta fica dourada, como a carta na mesa.
function CartaCurta({ carta, manilha = false }: { carta: Card; manilha?: boolean }) {
  const s = suitOf(carta);
  return (
    <span className={'carta-curta' + (s === 'h' || s === 'd' ? ' red' : '') + (manilha ? ' manilha' : '')}>
      {rankOf(carta)}{SIMBOLO[s]}{manilha && <span className="so-leitor">, manilha</span>}
    </span>
  );
}

const ORDINAL = ['1ª', '2ª', '3ª'];

// Histórico da mão: as rodadas que já fecharam, quem jogou o quê na rodada em andamento (com GANHANDO ou EMPACHADO),
// os sinais que você recebeu nesta mão e de quem é a vez.
export function HistoricoDaMao({ h, eu, nome, meu, sinais, vez, segundos, assistindo }: {
  h: Mao | null; eu: Seat; nome: (s: Seat) => string; meu: Team; sinais: SinalRecebido[]; vez: Seat | null; segundos: number | null; assistindo: boolean;
}) {
  const rodadas = h?.rounds ?? [], atual = rodadas[rodadas.length - 1];
  const fechadas = rodadas.filter((r) => r.result !== null);
  const emAndamento = atual && atual.result === null ? atual : null;
  const estado = emAndamento && h && emAndamento.plays.length > 1 ? estadoDaRodada(emAndamento, h.manilha) : null;
  const corDe = (s: Seat) => (team(s) === meu ? 'var(--nos-400)' : 'var(--eles-400)');
  // Com a coluna baixa, a lista rola: a jogada mais nova fica sempre à vista.
  const lista = useRef<HTMLUListElement>(null);
  const linhas = fechadas.length + (emAndamento?.plays.length ?? 0) + sinais.length;
  useLayoutEffect(() => { const ul = lista.current; if (ul) ul.scrollTop = ul.scrollHeight; }, [linhas]);
  return (
    <section className="painel historico-mao" aria-label="Histórico da mão">
      <header><h2>Histórico da mão</h2>{h && <span>Rodada {Math.min(3, rodadas.length)} de 3</span>}</header>
      <ul ref={lista}>
        {fechadas.map((r, i) => {
          const quem = r.winnerSeat, carta = quem !== null ? r.plays.find((p) => p.seat === quem)?.card : null;
          return (
            <li key={'r' + i} className="fechada">
              <span className="ordinal">{ORDINAL[i]}</span>
              {r.result === 'tie' || quem === null
                ? <span className="texto">empachou</span>
                : <span className="texto"><b style={{ color: corDe(quem) }}>{nome(quem)}</b> levou{carta ? <> com <CartaCurta carta={carta} manilha={rankOf(carta) === h?.manilha} /></> : null}</span>}
            </li>
          );
        })}
        {emAndamento?.plays.map((p) => {
          const ganhando = estado?.ganhando === p.seat, empachado = !!estado?.empachados.includes(p.seat);
          // A carta coberta nunca aparece para os outros; a sua você vê, marcada como coberta.
          const carta = p.hidden && (p.seat !== eu || assistindo) ? null : p.card;
          return (
            <li key={'j' + p.seat}>
              <i className="ponto" style={{ background: corDe(p.seat) }} aria-hidden="true" />
              <span className="texto"><b>{nome(p.seat)}</b> jogou</span>
              {ganhando && <span className="tag gold">GANHANDO</span>}
              {empachado && <span className="tag dark empachado">EMPACHADO</span>}
              {p.hidden && <span className="carta-curta coberta">coberta</span>}
              {carta && <CartaCurta carta={carta} manilha={!p.hidden && rankOf(carta) === h?.manilha} />}
            </li>
          );
        })}
        {sinais.map((x) => (
          <li key={'s' + x.id} className="sinal">
            <i className="ponto" style={{ background: corDe(x.seat) }} aria-hidden="true" />
            <span className="texto"><b>{nome(x.seat)}</b> passou sinal</span>
            <span className="gesto-recebido" aria-label={CARTA_DO_SINAL[x.gesture]}>{GESTO_EMOJI[x.gesture]}<small>{CARTA_DO_SINAL[x.gesture]}</small></span>
          </li>
        ))}
      </ul>
      {!h && <p className="vazio">As cartas ainda vão ser dadas.</p>}
      {/* Antes da primeira jogada, o painel mostra a vira e a manilha da mão, em vez de ficar vazio. */}
      {h && linhas === 0 && (
        <dl className="inicio-da-mao">
          <div><dt>Vira</dt><dd><CartaCurta carta={h.vira} /></dd></div>
          <div><dt>Manilha</dt><dd>{h.manilha}</dd></div>
        </dl>
      )}
      {vez !== null && (
        <p className="vez">
          <i className="ponto" style={{ background: corDe(vez) }} aria-hidden="true" />
          <span className="texto">{vez === eu && !assistindo ? <><b>Você</b> joga agora</> : <>Vez de <b>{nome(vez)}</b></>}</span>
          {segundos !== null && <b className={'segundos' + (segundos <= 5 ? ' acabando' : '')}>{segundos}s</b>}
        </p>
      )}
    </section>
  );
}

// Seu trio: rosto, nome, quantas cartas cada um tem e de quem é a vez.
export function SeuTrio({ h, eu, meu, nome, lugares, vez }: {
  h: Mao | null; eu: Seat; meu: Team; nome: (s: Seat) => string; lugares: readonly (LugarPublico | null)[]; vez: Seat | null;
}) {
  const trio = ([0, 1, 2, 3, 4, 5] as Seat[]).filter((s) => team(s) === meu).sort((a, b) => (a === eu ? 1 : b === eu ? -1 : a - b));
  return (
    <section className="painel seu-trio" aria-label="Seu trio">
      <header><h2>Seu trio</h2><span className="chip-nos">NÓS</span></header>
      <ul>
        {trio.map((s) => {
          const l = lugares[s] ?? null, n = h?.cardCounts[s] ?? 0;
          return (
            <li key={s}>
              <Rosto perfil={l?.perfil} usuario={l?.tipo === 'pessoa' ? l.id : undefined} bot={ehBot(l)} cor="var(--nos-400)" tamanho={34} />
              <b>{nome(s)}</b>
              <span className={vez === s ? 'vez' : undefined}>{vez === s ? (s === eu ? 'Sua vez' : 'Na vez') : h ? `${n} ${n === 1 ? 'carta' : 'cartas'}` : ''}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
